import React from "react";
import {connect} from "react-redux";
import {
    Button,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Input,
    message,
    Modal,
    Row,
    Select,
    Space,
    Tag
} from "antd";
import {loadAllDomains} from "../../actions/domains";
import {displayErrors} from "../../libs/utils";
import {ExclamationCircleOutlined} from "@ant-design/icons";
import {createWRMProject} from "../../actions/wayback_machine_restoration";
import moment from "moment";

class WaybackMachineRestorationAddProjectModal extends React.Component {
    state = {
        selectedDate: moment(),
        approximate_date: moment().format("YYYY-MM-DD"),
        url_list: [],
        viewUrlListModal: false,
        loading: false,
        domainList: [],
        domain: null,
        loadingDomains: false,
        domainsLoaded: false,
        url_structure: 'https://',
        internal_urls: 'RELATIVE',
        date_range: '1'
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevProps.showModal && this.props.showModal && !this.state.domainsLoaded) {
            this.setState({ loadingDomains: true });

            this.props.loadAllDomains(1, 9999, (res) => {
                let domains = res.data.data.map((domain) => {
                    return { label: domain.domain, value: domain.guid };
                });

                this.setState({ domainList: domains, loadingDomains: false, domainsLoaded: true });
            }, (err) => {
                if(typeof err.response !== 'undefined') {
                    this.setState({ loadingDomains: false, domainsLoaded: true });
                    displayErrors(err.response.data);
                }
            });
        }
    }

    submitRequest() {
        if(this.state.domain === null) {
            return message.error('Please select a domain.');
        }

        const { confirm } = Modal;

        confirm({
            title: 'Attention',
            centered: true,
            okText: 'I understand!',
            okType: 'danger',
            okButtonProps: {
                loading: this.state.updatingHostingAccount
            },
            icon: <ExclamationCircleOutlined />,
            content: <div>
                <p><b>Heads up before you continue:</b></p>
                <p>Restoring a website will <b>automatically upload the restored version</b> to your hosting account. This process <b>may replace your existing files</b>, so please make sure you have backups if needed.</p>
                <p>By clicking <b>"I understand"</b>, you confirm that:</p>
                <ul>
                    <li>You are aware that restoring your website may overwrite current files.</li>
                    <li>You understand that this feature is <b>in beta</b>, which means it may not work perfectly in all situations.</li>
                </ul>
            </div>,
            onOk: () => {
                this.setState({ loading: true });

                let data = {
                    approximate_date: this.state.approximate_date,
                    url_list: this.state.url_list,
                    domain: this.state.domain,
                    url_structure: this.state.url_structure,
                    internal_urls: this.state.internal_urls,
                    date_range: this.state.date_range,
                };

                this.props.createWRMProject(data, (res) => {
                    this.setState({ loading: false });
                    message.success('Wayback Machine restoration project successfully created!');
                    this.onClose();
                    this.props.history.push('/website-restoration/' + res.data.guid);
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        this.setState({ loading: false });
                        displayErrors(err.response.data);
                    }
                });
            }
        })
    }

    onClose(reload = false) {
        this.setState({
            approximate_date: null,
            url_list: [],
            loading: false,
            domainList: [],
            domain: null,
            loadingDomains: false,
            domainsLoaded: false
        }, () => {
            this.props.close();
            if(reload) {
                this.props.reload();
            }
        });
    }

    render() {
        const { TextArea } = Input;
        const { Option } = Select;

        return <Modal
            centered
            title={<span>Wayback Machine Recovery Wizard <Tag color="gold">Beta</Tag></span>}
            open={this.props.showModal}
            footer={<Space>
                <Button disabled={this.state.loading} onClick={() => this.onClose()}>Close</Button>
                <Button disabled={this.state.loading}  loading={this.state.loading} type='primary' onClick={() => this.submitRequest()}>Create Project</Button>
            </Space>}
            onCancel={() => this.onClose()}
            destroyOnClose={true}>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form layout='vertical'>
                        <Form.Item label='Select domain to restore:'>
                            <Select
                                options={this.state.domainList}
                                loading={this.state.loadingDomains}
                                disabled={this.state.loadingDomains}
                                showSearch allowClear
                                style={{width: '100%'}}
                                placeholder='Select domain'
                                notFoundContent={<span style={{color: 'gray'}}>No domains found!</span>}
                                onChange={(value) => this.setState({ domain: value })}
                                filterOption={(input, option) => {
                                    if(typeof option !== 'undefined') {
                                        return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                    }
                                    return false;
                                }} />
                        </Form.Item>
                    </Form>
                </Col>
            </Row>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Descriptions bordered size='small' layout='horizontal' column={1}>
                        <Descriptions.Item label='Restoration date' labelStyle={{width: '50%'}}>
                            <DatePicker value={this.state.selectedDate} format='YYYY-MM-DD' style={{width: '100%'}}
                                        onChange={(value) => {
                                            this.setState({ approximate_date: value.format('YYYY-MM-DD'), selectedDate: value });
                                        }}/>
                        </Descriptions.Item>
                        <Descriptions.Item label='Date range' labelStyle={{width: '50%'}}>
                            <Select value={this.state.date_range} onChange={(value) => this.setState({ date_range: value }) } style={{width: '100%'}}>
                                <Option value='1'>Date minus 1 month</Option>
                                <Option value='2'>Date minus 2 months</Option>
                                <Option value='3'>Date minus 3 months</Option>
                                <Option value='4'>Date minus 4 months</Option>
                                <Option value='5'>Date minus 5 months</Option>
                                <Option value='6'>Date minus 6 months</Option>
                                <Option value='7'>Date minus 7 months</Option>
                                <Option value='8'>Date minus 8 months</Option>
                                <Option value='9'>Date minus 9 months</Option>
                                <Option value='10'>Date minus 10 months</Option>
                                <Option value='11'>Date minus 11 months</Option>
                                <Option value='12'>Date minus 12 months</Option>
                            </Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='URL format' labelStyle={{width: '50%'}}>
                            <Select value={this.state.url_structure} onChange={(value) => this.setState({ url_structure: value }) } style={{width: '100%'}}>
                                <Option value='http://'>http://</Option>
                                <Option value='http://www.'>http://www.</Option>
                                <Option value='https://'>https://</Option>
                                <Option value='https://www.'>https://www.</Option>
                            </Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='Internal URL format' labelStyle={{width: '50%'}}>
                            <Select value={this.state.internal_urls} onChange={(value) => this.setState({ internal_urls: value }) } style={{width: '100%'}}>
                                <Option value='RELATIVE'>Relative URLs</Option>
                                <Option value='ABSOLUTE'>Absolute URLs</Option>
                            </Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='URL list' labelStyle={{width: '50%'}}>
                            <Button onClick={() => this.setState({ viewUrlListModal: true })} size='small'>{this.state.url_list.length > 0 ? this.state.url_list.length + ' URLs selected' : 'Enter URLs'}</Button>
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
            </Row>

            <Modal
                title='URL List'
                open={this.state.viewUrlListModal}
                centered
                footer={<Button onClick={() => this.setState({viewUrlListModal: false})}>Close</Button>}
                onCancel={() => this.setState({viewUrlListModal: false})}>
                <TextArea rows={10} onChange={(e) => this.setState({url_list: e.target.value.trim().split('\n')})}/>
                <Row>
                    <Col md={16}>
                        <div><small>Optional. Enter one URL per row, max 500 URLs.</small></div>
                        <div><small>Only these URLs will be restored, when they are available.</small></div>
                    </Col>
                    <Col md={8} className='text-right'>
                        <small>{this.state.url_list.length} URL{this.state.url_list.length !== 1 ? 's' : ''}</small>
                    </Col>
                </Row>
            </Modal>
        </Modal>;
    }
}

export default connect(null, { createWRMProject, loadAllDomains })(WaybackMachineRestorationAddProjectModal);